// components/tabs/ConstellationView.js
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Hexagon, Star, Globe } from 'lucide-react';
import { SiGithub, SiX, SiDiscord } from '@icons-pack/react-simple-icons';
import { formatNumber } from '../../utils/formatters';

interface StreakData {
  history: number[];
}

// Helper to get last active time
const getLastActiveTime = (userData) => {
  if (!userData) return 'Never';
  
  const timestamps = [];
  
  if (userData.pointsHistory && userData.pointsHistory.length > 0) {
    userData.pointsHistory.forEach(entry => {
      if (entry.timestamp) timestamps.push(entry.timestamp);
    });
  }
  
  if (userData.streaks) {
    (Object.values(userData.streaks) as StreakData[]).forEach(streak => {
      if (streak.history && streak.history.length > 0) {
        streak.history.forEach(ts => timestamps.push(ts));
      }
    });
  }
  
  if (timestamps.length === 0) return 'Unknown';
  
  const mostRecent = Math.max(...timestamps);
  const now = Math.floor(Date.now() / 1000);
  const diff = now - mostRecent;
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(mostRecent * 1000).toLocaleDateString();
};

// Get role metadata for modal
const getRoleInfo = (role) => {
  const roleMap = {
    'GitHub Verified': { icon: SiGithub, color: '#333333', description: 'Verified GitHub developer with proven contributions' },
    'X Verified': { icon: SiX, color: '#1DA1F2', description: 'Active X (Twitter) community member' },
    'Discord': { icon: SiDiscord, color: '#5865F2', description: 'Active Discord community participant' },
    'ArNS Holder': { icon: Globe, color: '#FF4747', description: 'Owns an Arweave Name System domain' },
    'amVerified': { icon: Shield, color: '#15FF75', description: 'Fully verified amPassport holder' },
    'OG': { icon: Star, color: '#FFB800', description: 'Original community member' },
  };
  
  for (const [key, info] of Object.entries(roleMap)) {
    if (role.toLowerCase().includes(key.toLowerCase())) return info;
  }
  
  return { icon: Hexagon, color: '#6B7280', description: 'Active community role' };
};

const ConstellationView = ({ userData, currentAvatar }) => {
  const [selectedRole, setSelectedRole] = useState(null);
  const roles = userData?.roles || [];
  
  // Calculate grid layout based on number of roles
  const getGridLayout = (roleCount) => {
    if (roleCount <= 4) {
      return {
        left: roles.slice(0, 2),
        right: roles.slice(2, 4),
        top: [],
        bottom: []
      };
    } else if (roleCount <= 8) {
      const half = Math.ceil(roleCount / 2);
      return {
        left: roles.slice(0, Math.floor(half / 2)),
        right: roles.slice(Math.floor(half / 2), half),
        top: roles.slice(half, half + Math.ceil((roleCount - half) / 2)),
        bottom: roles.slice(half + Math.ceil((roleCount - half) / 2))
      };
    } else {
      const perSide = Math.ceil(roleCount / 4);
      return {
        left: roles.slice(0, perSide),
        right: roles.slice(perSide, perSide * 2),
        top: roles.slice(perSide * 2, perSide * 3),
        bottom: roles.slice(perSide * 3)
      };
    }
  };

  const layout = getGridLayout(roles.length);

  // Role card component - original design with click handler
  const RoleCard = ({ role, delay = 0 }) => (
    <motion.div
      className="relative w-[200px] p-4 bg-[#0A0E1B]/40 backdrop-blur-md rounded-xl border border-[#15FF75]/20
                 hover:border-[#15FF75]/40 transition-all duration-300 group cursor-pointer"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay, duration: 0.3 }}
      whileHover={{ scale: 1.02 }}
      onClick={() => setSelectedRole(role)}
      style={{
        boxShadow: '0 4px 20px rgba(21, 255, 117, 0.1)',
      }}
    >
      <div className="text-center">
        <h3 className="text-white font-bold text-lg mb-2">{role}</h3>
        <div className="w-12 h-0.5 bg-gradient-to-r from-[#15FF75] to-[#2CF98E] mx-auto 
                        group-hover:w-16 transition-all duration-300" />
      </div>
      
      {/* Subtle glow effect on hover */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-[#15FF75]/0 to-[#2CF98E]/0 
                      group-hover:from-[#15FF75]/5 group-hover:to-[#2CF98E]/5 transition-all duration-300 pointer-events-none" />
    </motion.div>
  );

  return (
    <div className="relative w-full h-[600px] flex items-center justify-center p-12">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" 
          style={{
            backgroundImage: `
              linear-gradient(90deg, #15FF75 1px, transparent 1px),
              linear-gradient(#15FF75 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
      </div>

      {/* Main grid container */}
      <div className="relative grid grid-cols-[1fr_auto_1fr] grid-rows-[1fr_auto_1fr] gap-8 w-full max-w-6xl">
        
        {/* Top roles */}
        <div className="col-span-3 flex justify-center gap-6">
          {layout.top.map((role, i) => (
            <RoleCard key={`top-${i}`} role={role} delay={i * 0.1} />
          ))}
        </div>

        {/* Left roles */}
        <div className="flex flex-col justify-center gap-6 items-end">
          {layout.left.map((role, i) => (
            <RoleCard key={`left-${i}`} role={role} delay={0.2 + i * 0.1} />
          ))}
        </div>

        {/* Center Identity Card - Original design */}
        <motion.div
          className="relative w-[300px] p-6 bg-[#0A0E1B]/60 backdrop-blur-xl rounded-2xl
                     border-2 border-transparent bg-clip-padding"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          style={{
            backgroundImage: 'linear-gradient(135deg, rgba(10,14,27,0.9), rgba(10,14,27,0.9)), linear-gradient(135deg, #15FF75, #2CF98E)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
            boxShadow: '0 8px 32px rgba(21, 255, 117, 0.2)',
          }}
        >
          {/* Avatar */}
          <div className="flex justify-center mb-4">
            <motion.div
              className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-[#15FF75]/20 to-[#2CF98E]/20 
                         border-2 border-[#15FF75]/30 flex items-center justify-center"
              whileHover={{ scale: 1.05 }}
            >
              {currentAvatar ? (
                <img src={currentAvatar} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <Shield className="w-10 h-10 text-[#15FF75]" />
              )}
            </motion.div>
          </div>

          {/* Identity info */}
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">AO Pathfinder</h2>
            
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-[#8AA5A2]">LEVEL</span>
                <span className="text-[#15FF75] font-semibold">{Math.floor(userData?.points / 1000) || 0}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-[#8AA5A2]">REPUTATION</span>
                <span className="text-[#15FF75] font-semibold">{formatNumber(userData?.points || 0)}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-[#8AA5A2]">LAST ACTIVE</span>
                <span className="text-white">{getLastActiveTime(userData)}</span>
              </div>
            </div>
            
            {/* Points breakdown */}
            {userData?.pointsStats && Object.keys(userData.pointsStats).length > 0 && (
              <div className="mt-4 pt-4 border-t border-[#15FF75]/20">
                <div className="text-xs text-[#8AA5A2] mb-2 uppercase">Points Breakdown</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {Object.entries(userData.pointsStats)
                    .filter(([_, value]) => typeof value === "number" && value > 0)
                    .sort((a, b) => (b[1] as number) - (a[1] as number))
                    .slice(0, 4)
                    .map(([category, value]) => (
                      <div key={category} className="flex justify-between">
                        <span className="text-[#8AA5A2] capitalize">{category}:</span>
                        <span className="text-[#15FF75] font-medium">{formatNumber(value)}</span>
                      </div>
                    ))
                  }
                </div>
              </div>
            )}
          </div>

          {/* Center glow effect */}
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#15FF75]/10 to-transparent pointer-events-none" />
        </motion.div>

        {/* Right roles */}
        <div className="flex flex-col justify-center gap-6 items-start">
          {layout.right.map((role, i) => (
            <RoleCard key={`right-${i}`} role={role} delay={0.4 + i * 0.1} />
          ))}
        </div>

        {/* Bottom roles */}
        <div className="col-span-3 flex justify-center gap-6">
          {layout.bottom.map((role, i) => (
            <RoleCard key={`bottom-${i}`} role={role} delay={0.6 + i * 0.1} />
          ))}
        </div>
      </div>

      {/* Corner text */}
      <div className="absolute top-4 right-4 text-[#8AA5A2] text-sm bg-[#0A0E1B]/80 px-3 py-1 rounded-lg backdrop-blur-sm z-10">
        ROLES: {roles.length}
      </div>

      {/* Role Detail Modal */}
      <AnimatePresence>
        {selectedRole && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedRole(null)}
          >
            <motion.div
              className="bg-gradient-to-br from-[#0A0E1B] to-[#0D1220] p-6 rounded-2xl 
                       border border-[#15FF75]/30 max-w-sm w-full"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {(() => {
                const info = getRoleInfo(selectedRole);
                const Icon = info.icon;
                return (
                  <>
                    <div className="flex items-center gap-4 mb-4">
                      <div 
                        className="w-16 h-16 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${info.color}20` }}
                      >
                        <Icon className="w-8 h-8" style={{ color: info.color }} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-[#E8FDF2]">{selectedRole}</h3>
                        <p className="text-sm text-[#8AA5A2]">Active Role</p>
                      </div>
                    </div>
                    
                    <p className="text-sm text-[#8AA5A2] mb-4">
                      {info.description}
                    </p>
                    
                    <button
                      onClick={() => setSelectedRole(null)}
                      className="w-full px-4 py-2 bg-[#1A1F2E] hover:bg-[#242938] rounded-lg 
                               text-[#E8FDF2] transition-all"
                    >
                      Close
                    </button>
                  </>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ConstellationView;